<script setup>
import {
  computed,
  onMounted,
  ref,
  watch,
  nextTick,
  defineProps,
  onUpdated,
} from 'vue';
import { useStore } from 'vuex';
import { ModuleTourGuide } from '@/stores/moduleConst.js';
import MsPopover from '@/components/popover/MsPopover.vue';

const store = useStore();

const tourGuideTargetRef = ref(null);
const popoverRef = ref(null);
const isActivePopover = ref(false);
const isReady = ref(false);
const tmpStep = ref(null);

/**
 * Có đang bấm tiếp tục hoặc trở về ko ko
 */
const isClickNextOrBack = ref(false);

onUpdated(() => {
  //udpate
  if (isActive.value) {
    isActivePopover.value = true;
  }
});

/**
 * Trạng thái đóng mở
 * @author: BMThang(29/03/2023)
 */
const isActive = computed(() => {
  return store.state[ModuleTourGuide].isActive;
});

/**
 * Danh sách các bước của tour guide
 * @author: BMThang(29/03/2023)
 */
const steps = computed(() => {
  return store.state[ModuleTourGuide].steps;
});

/**
 * Bước hiện tại
 * @author: BMThang(29/03/2023)
 */
const step = computed(() => {
  return store.state[ModuleTourGuide].step;
});

/**
 * Thông tin bước hiện tại
 * @author: BMThang(29/03/2023)
 */
const selectedStep = computed(() => {
  if (step.value <= steps.value.length) {
    return steps.value[step.value - 1];
  }
  return null;
});

/**
 * Style của target element
 * @author: BMThang(29/03/2023)
 */
const targetStyle = computed(() => {
  if (selectedStep.value) {
    return {
      top: `${selectedStep.value.top}px`,
      left: `${selectedStep.value.left}px`,
      width: `${selectedStep.value.width}px`,
      height: `${selectedStep.value.height}px`,
    };
  }
  return {};
});

/**
 * Position của popover
 * @author: BMThang(29/03/2023)
 */
const popoverPosition = computed(() => {
  if (selectedStep.value) {
    return selectedStep.value.position;
  }
  return {};
});

/**
 * Tiêu đề popover
 * @author: BMThang(29/03/2023)
 */
const title = computed(() => {
  if (selectedStep.value?.title) {
    return selectedStep.value.title;
  }
  return '';
});

/**
 * Msg trong popover
 * @author: BMThang(29/03/2023)
 */
const msg = computed(() => {
  if (selectedStep.value?.msg) {
    return selectedStep.value.msg;
  }
  return '';
});

/**
 * Hàm thực hiện khi nhấn tiếp tục
 * @author: BMThang(29/03/2023)
 */
const nextStepFunc = computed(() => {
  if (typeof selectedStep.value?.nextStepFunc === 'function') {
    return selectedStep.value.nextStepFunc;
  }
  return () => {};
});

/**
 * Hàm thực hiện khi nhấn quay lại
 * @author: BMThang(29/03/2023)
 */
const prevStepFunc = computed(() => {
  if (typeof selectedStep.value?.prevStepFunc === 'function') {
    return selectedStep.value.prevStepFunc;
  }
  return () => {};
});

/**
 * Hiển thị btn Tiếp tục
 * @author: BMThang(29/03/2023)
 */
const isShowContinueBtn = computed(() => {
  if (step.value < steps.value.length) {
    return true;
  }
  return false;
});

/**
 * Hiển thị btn Đóng
 */
const isShowCloseBtn = computed(() => {
  if (step.value < steps.value.length) {
    return true;
  }
  return false;
});

/**
 * Hiển thị btn Quay lại
 * @author: BMThang(29/03/2023)
 */
const isShowBackBtn = computed(() => {
  if (step.value > 1 && step.value < steps.value.length) {
    return true;
  }
  if (step.value === steps.value.length && steps.value.length !== 1) {
    return true;
  }
  return false;
});

/**
 * Hiển thị btn Tôi đã hiểu
 * @author: BMThang(29/03/2023)
 */
const isShowUnderstandBtn = computed(() => {
  if (step.value === steps.value.length) {
    return true;
  }
  return false;
});

/**
 * Mở popover
 * @author: BMThang(29/03/2023)
 */
const openPopover = () => {
  isActivePopover.value = true;
};

/**
 * Đóng popover
 * @author: BMThang(29/03/2023)
 */
const closePopover = () => {
  isActivePopover.value = false;

  let targetStep = tmpStep.value;
  if (selectedStep.value) targetStep = selectedStep.value;

  if (tmpStep.value.key) {
    store.state.tourGuide.tourLiveChat =
      tmpStep.value.key === 'Dashboard' ? true : false;
  }

  if (!targetStep) return;

  // Lưu bước hiện tại
  // step lúc này là step của bước mới nhất
  store.dispatch(`${ModuleTourGuide}/saveTourGuideStep`, {
    tourGuideKey: targetStep.key,
    step: step.value,
  });
  tmpStep.value = null;
};

/**
 * Đóng tour guide
 * @author: BMThang(29/03/2023)
 */
const closeTourGuide = () => {
  tmpStep.value = steps.value[steps.value.length - 1];
  store.commit(`${ModuleTourGuide}/setStep`, steps.value.length + 1);
  closePopover();
  store.commit(`${ModuleTourGuide}/closeTourGuide`);
};

/**
 * Tính vị trí popover
 * @author: BMThang(29/03/2023)
 */
const calculatePopoverPosition = () => {
  if (!isClickNextOrBack.value) return;
  openPopover();
  isClickNextOrBack.value = false;
  nextTick(() => {
    popoverRef.value?.calculatePopoverPosition();
  });
};

/**
 * Chuyển bước tiếp theo
 * @author: BMThang(29/03/2023)
 */
const handleClickContinue = () => {
  nextStepFunc.value();
  store.commit(`${ModuleTourGuide}/setStep`, step.value + 1);
  closePopover();
  isClickNextOrBack.value = true;
};

/**
 * Chuyển bước trước đó
 * @author: BMThang(29/03/2023)
 */
const handleClickBack = () => {
  prevStepFunc.value();
  store.commit(`${ModuleTourGuide}/setStep`, step.value - 1);
  closePopover();
  isClickNextOrBack.value = true;
};

/**
 * Bấm đã hiểu
 * @author: BMThang(29/03/2023)
 */
const handleClickUnderstand = () => {
  tmpStep.value = selectedStep.value;
  store.commit(`${ModuleTourGuide}/setStep`, step.value + 1);
  closePopover();
  store.commit(`${ModuleTourGuide}/closeTourGuide`);
};

// Khi active thì mở popover lên
watch(
  () => isActive.value,
  (newVal) => {
    if (newVal) {
      openPopover();
    }
  },
  {
    flush: 'post',
  }
);

onMounted(() => {
  // Chỉ hiện popover khi tour guide đã mounted
  isReady.value = true;
});
</script>

<template>
  <div
    id="ms-tour-guide"
    :class="['ms-tour-guide', { 'ms-tour-guide-active': isActive }]"
  >
    <div
      ref="tourGuideTargetRef"
      class="ms-tour-guide-target"
      :style="targetStyle"
      @transitionend="calculatePopoverPosition"
    />
    <ms-popover
      v-if="isReady"
      ref="popoverRef"
      :is-active="isActivePopover"
      :target="tourGuideTargetRef"
      :has-arrow="true"
      :is-in-tour-guide="true"
      :position="popoverPosition"
      :width="412"
      :custom-style="{
        padding: 0,
      }"
      transition="ms-side-bottom-top"
    >
      <div class="ms-tour-guide-popover">
        <!-- Header -->
        <div class="ms-tour-guide-popover-header">
          <div class="ms-tour-guide-popover-title">
            {{ title }}
          </div>
          <div
            v-tooltip="$t('i18nCommon.command.close')"
            class="icon20 ic_close-grey-16 ms-tour-guide-popover-close"
            @click="closeTourGuide"
          />
        </div>

        <!-- Body -->
        <div class="ms-tour-guide-popover-body" v-html="msg" />

        <!-- Footer -->
        <div class="ms-tour-guide-popover-footer">
          <div class="ms-tour-guide-step">
            <span v-if="steps.length > 1">{{ `${step}/${steps.length}` }}</span>
          </div>
          <div class="ms-tour-guide-buttons">
            <ms-button
              v-if="isShowCloseBtn"
              :text="$t('i18nCommon.command.close')"
              type="secondary-outline"
              :has-border="true"
              @click="closeTourGuide"
            />
            <ms-button
              v-if="isShowBackBtn"
              :text="$t('i18nCommon.command.back')"
              type="secondary-outline"
              :has-border="true"
              @click="handleClickBack"
            />
            <ms-button
              v-if="isShowContinueBtn"
              :text="$t('i18nCommon.command.continue')"
              type="primary"
              @click="handleClickContinue"
            />
            <ms-button
              v-if="isShowUnderstandBtn"
              :text="$t('i18nCommon.command.understand')"
              type="primary"
              @click="handleClickUnderstand"
            />
          </div>
        </div>
      </div>
    </ms-popover>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.ms-tour-guide {
  opacity: 0;
  visibility: hidden;
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 105;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  &.ms-tour-guide-active {
    width: 100%;
    height: 100vh;
    opacity: 100;
    visibility: visible;
  }
  .ms-tour-guide-target {
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.66);
    position: absolute;
    transition: all 0.2s ease;
  }
}

.ms-tour-guide-popover {
  padding-top: 12px;
  .ms-tour-guide-popover-header {
    height: 36px;
    padding-left: 24px;
    padding-right: 24px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: $font-size-heading-2;
    font-weight: $font-weight-bold;
    .ms-tour-guide-popover-close {
      cursor: pointer;
      display: inline-block;
      transition: background-color 0.2s ease;
      padding: 18px;
      border-radius: 50%;
      &:hover {
        background-color: var(--grey-20);
      }
    }
  }
  .ms-tour-guide-popover-body {
    padding-right: 24px;
    padding-left: 24px;
  }
  .ms-tour-guide-popover-footer {
    margin-top: 28px;
    height: 60px;
    background-color: var(--grey-5);
    border-top: 1px solid var(--grey-60);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    .ms-tour-guide-buttons {
      display: flex;
      align-items: center;
      column-gap: 12px;
    }
  }
}
</style>